import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Play, Download, Bookmark, Trash2, Award, Calendar, CheckCircle2,
  Trophy, FileText, Video, Image, Mic, ExternalLink, Search, Crown,
  MapPin, Clock, ArrowRight, Sparkles
} from 'lucide-react'
import { masterclassService } from '../../services/masterclassService'
import { resolveImageUrl, resolveSpeakerPhoto } from '../../utils/imageUrl'
import { formatScheduleFallback } from '../../utils/masterclassSchedule'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'
import LoadingSpinner from '../../components/LoadingSpinner'
import DigitalEventPass from '../../components/masterclass/DigitalEventPass'

const BADGE_COLORS = {
  bronze:   'bg-amber-700/20 text-amber-400 border-amber-700/40',
  silver:   'bg-white/10 text-white/70 border-white/20',
  gold:     'bg-yellow-500/15 text-yellow-400 border-yellow-500/35',
  champion: 'bg-cinema-glow/15 text-cinema-glow border-cinema-glow/35',
}

const BADGE_LABELS = {
  bronze:   'Bronze Learner (5 attended)',
  silver:   'Silver Learner (15 attended)',
  gold:     'Gold Learner (30 attended)',
  champion: 'Champion Learner (50 attended)',
}

export default function StudentLibraryPage() {
  const { user } = useAuthStore()
  const [library, setLibrary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('attended')
  const [search, setSearch] = useState('')
  const [leaderboard, setLeaderboard] = useState([])
  const [upcoming, setUpcoming] = useState(null)
  const [registering, setRegistering] = useState(false)
  const [registered, setRegistered] = useState(null)

  useEffect(() => {
    document.title = 'My Masterclass Library | SMA Student Portal'
    loadLibrary()
    loadLeaderboard()
    loadUpcoming()
  }, [])

  const loadLibrary = async () => {
    setLoading(true)
    try {
      const data = await masterclassService.getMyLibrary()
      setLibrary(data)
    } catch (err) {
      toast.error('Failed to load library')
    } finally {
      setLoading(false)
    }
  }

  const loadLeaderboard = async () => {
    try {
      const data = await masterclassService.getLeaderboard(10)
      setLeaderboard(data.leaderboard || [])
    } catch (err) {
      console.error(err)
    }
  }

  const loadUpcoming = async () => {
    try {
      const data = await masterclassService.getUpcoming()
      setUpcoming(data.masterclass || null)
    } catch (err) {
      console.error(err)
    }
  }

  const handleViewCert = async (certNumber) => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/my-certificates/${certNumber}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to fetch certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 5000)
    } catch (err) {
      console.error(err)
      toast.error('Failed to view certificate')
    }
  }

  const handleDownloadCert = async (certNumber) => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/my-certificates/${certNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to download certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `SMA-Certificate-${certNumber}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      toast.error('Failed to download certificate')
    }
  }

  const handleStudentRegister = async () => {
    if (!upcoming) return
    setRegistering(true)
    try {
      const data = await masterclassService.studentRegister({
        masterclass_id: upcoming.id,
      })
      setRegistered(data.registration)
      toast.success('You\'re registered! Your digital pass is ready.')
    } catch (err) {
      toast.error(err.message || 'Registration failed')
    } finally {
      setRegistering(false)
    }
  }

  const removeBookmark = async (id) => {
    try {
      await masterclassService.deleteBookmark(id)
      toast.success('Bookmark removed')
      loadLibrary()
    } catch (err) {
      toast.error('Failed to remove bookmark')
    }
  }

  const trackDownload = async (resourceId) => {
    try {
      await masterclassService.trackDownload(resourceId)
    } catch (err) {
      console.error(err)
    }
  }

  const filteredAttended = (library?.attended || []).filter(a =>
    a.topic?.toLowerCase().includes(search.toLowerCase()) ||
    a.speaker_name?.toLowerCase().includes(search.toLowerCase())
  )

  const filteredVideos = (library?.videos || []).filter(v =>
    v.title?.toLowerCase().includes(search.toLowerCase()) ||
    v.masterclass_topic?.toLowerCase().includes(search.toLowerCase())
  )

  const filteredResources = (library?.attended || []).flatMap(a =>
    (a.resources || []).map(r => ({ ...r, masterclass: a }))
  ).filter(r =>
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.resource_type?.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) return <LoadingSpinner label="Loading library" />

  const emptyState = (msg) => (
    <div className="col-span-full text-center py-16">
      <p className="font-sans text-sm text-cinema-muted">{msg}</p>
    </div>
  )

  const formatDate = (d) => d && !isNaN(new Date(d).getTime())
    ? new Date(d).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : formatScheduleFallback()

  return (
    <div className="space-y-8">
      {/* Digital Pass modal */}
      {registered && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-12 px-4">
          <div className="w-full max-w-2xl">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">🎉</div>
              <span className="cinema-label">Registration Confirmed</span>
              <h2 className="font-display text-2xl text-white font-light mt-3">
                You\'re <span className="text-cinema-gold">Registered!</span>
              </h2>
              <p className="font-sans text-sm text-cinema-muted mt-2">
                Your digital pass has been generated. See you on the last Friday of the month! 🚀
              </p>
            </div>
            <DigitalEventPass registration={registered} />
            <button
              onClick={() => setRegistered(null)}
              className="mt-6 w-full cinema-btn-ghost text-xs py-3"
            >
              ← Back to Library
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="cinema-page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">My Masterclass Library</h1>
          <p className="font-sans text-xs text-cinema-muted mt-1">Your recordings, certificates, downloads, and bookmarks</p>
        </div>
        <Link to="/masterclass" className="cinema-btn-ghost text-xs px-5 py-2.5 min-h-0 shrink-0 inline-flex items-center gap-2">
          <Mic size={13} /> Browse All Masterclasses
        </Link>
      </div>

      {/* Upcoming Masterclass Registration */}
      {upcoming && (
        <div className="pl-4 border-l-2 border-cinema-glow/40">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            {/* Left: details */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={14} className="text-cinema-glow" />
                <span className="cinema-label text-cinema-glow">Next Masterclass · Register Now</span>
              </div>
              <h2 className="font-display text-xl text-white font-light leading-snug mb-3">
                {upcoming.topic}
              </h2>
              {upcoming.speaker_name && (
                <p className="font-sans text-sm text-cinema-gold mb-4">
                  with {upcoming.speaker_name}{upcoming.speaker_title ? ` · ${upcoming.speaker_title}` : ''}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-xs font-sans text-cinema-muted">
                <span className="flex items-center gap-1.5">
                  <Calendar size={12} className="text-cinema-glow" /> {formatDate(upcoming.event_date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={12} className="text-cinema-glow" /> {upcoming.start_time || '5:00 PM'}
                </span>
                <span className="flex items-center gap-1.5">
                  <MapPin size={12} className="text-cinema-glow" /> {upcoming.venue || 'SMA, Kampala'}
                </span>
              </div>
            </div>
            {/* Right: register button */}
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={handleStudentRegister}
                disabled={registering}
                className="cinema-btn-primary text-sm px-8 py-4 min-h-0 flex items-center gap-2.5 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {registering ? (
                  <><LoadingSpinner size="sm" inline /> Registering…</>
                ) : (
                  <>Register Now <ArrowRight size={16} /></>
                )}
              </button>
              <p className="font-sans text-xs text-cinema-muted text-center max-w-[200px]">
                One-click registration. No form needed — your details are already on file.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
        {[
          { label: 'Attended',     value: library?.attended?.length || 0 },
          { label: 'Certificates', value: library?.certificates?.length || 0 },
          { label: 'Bookmarks',    value: library?.bookmarks?.length || 0 },
          { label: 'Downloads',    value: library?.downloads?.length || 0 },
        ].map(({ label, value }) => (
          <div key={label} className="cinema-stat">
            <span className="cinema-stat-label">{label}</span>
            <span className="cinema-stat-value">{value}</span>
          </div>
        ))}
      </div>

      {/* Tabs + content panel */}
      <div className="pt-8 border-t border-white/5">
        {/* Tab bar + search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
          <div className="flex overflow-x-auto gap-1 no-scrollbar">
            {[
              { id: 'attended',    label: 'Attended',    Icon: CheckCircle2 },
              { id: 'videos',      label: 'Videos',      Icon: Video },
              { id: 'certificates',label: 'Certificates',Icon: Award },
              { id: 'bookmarks',   label: 'Bookmarks',   Icon: Bookmark },
              { id: 'downloads',   label: 'Downloads',   Icon: Download },
              { id: 'leaderboard', label: 'Leaderboard', Icon: Trophy },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans whitespace-nowrap transition-all border-b-2 -mb-px ${
                  activeTab === id
                    ? 'border-cinema-glow text-cinema-glow'
                    : 'border-transparent text-cinema-muted hover:text-white'
                }`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <div className="relative shrink-0">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-48 pl-8 pr-4 py-1.5 text-xs font-sans bg-white/5 border border-white/10 text-white focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"
            />
          </div>
        </div>

        <div>
          {/* ── Attended ── */}
          {activeTab === 'attended' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAttended.length === 0 ? emptyState('No attended masterclasses yet. Register and attend an event to unlock resources.') : (
                filteredAttended.map((session) => (
                  <div key={session.id} className="cinema-glass border border-white/6 rounded-sm overflow-hidden group hover:border-cinema-gold/20 transition-all">
                    <div className="h-36 bg-white/3 relative overflow-hidden">
                      {session.banner_url ? (
                        <img src={resolveImageUrl(session.banner_url)} alt={session.topic} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Mic size={32} className="text-cinema-muted/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <div className="p-4">
                      <p className="cinema-label mb-1">{session.category || 'Masterclass'}</p>
                      <h3 className="font-display text-sm text-white font-light leading-snug mb-2">
                        <Link to={`/masterclass/${session.slug || session.id}`} className="hover:text-cinema-glow transition-colors">
                          {session.topic}
                        </Link>
                      </h3>
                      <p className="flex items-center gap-1.5 font-sans text-xs sm:text-sm text-cinema-muted mb-3">
                        <Calendar size={13} />
                        {new Date(session.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {session.recording_url && (
                          <a href={session.recording_url} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-sans px-3 py-1.5 bg-cinema-glow/10 text-cinema-glow border border-cinema-glow/25 hover:bg-cinema-glow/20 transition-colors font-medium">
                            <Play size={12} /> Recording
                          </a>
                        )}
                        {session.highlight_video_url && (
                          <a href={session.highlight_video_url} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-sans px-3 py-1.5 cinema-glass border border-white/10 text-cinema-muted hover:text-white transition-colors font-medium">
                            <Video size={12} /> Highlights
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Videos ── */}
          {activeTab === 'videos' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVideos.length === 0 ? emptyState('No videos available yet.') : (
                filteredVideos.map((video) => (
                  <a
                    key={video.id}
                    href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                    target="_blank" rel="noreferrer"
                    className="group cinema-glass border border-white/6 rounded-sm overflow-hidden hover:border-cinema-gold/20 transition-all"
                  >
                    <div className="aspect-video relative overflow-hidden bg-white/3">
                      <img
                        src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                        alt={video.title} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/30 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-cinema-glow/20 border border-cinema-glow/40 flex items-center justify-center">
                          <Play size={16} className="text-white fill-white ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="cinema-label mb-1">{video.category || 'Video'}</p>
                      <h3 className="font-display text-base text-white font-light leading-snug">{video.title}</h3>
                      <p className="font-sans text-xs text-cinema-muted mt-1">{video.masterclass_topic}</p>
                    </div>
                  </a>
                ))
              )}
            </div>
          )}

          {/* ── Certificates ── */}
          {activeTab === 'certificates' && (
            <div className="cinema-list">
              {(library?.certificates || []).length === 0 ? emptyState('No certificates yet. Attend a masterclass and check in to receive one.') : (
                (library?.certificates || []).map((cert) => {
                  return (
                  <div key={cert.id} className="cinema-list-row justify-between">
                    <div className="flex items-start gap-3">
                      <div className="text-yellow-400 shrink-0"><Award size={18} /></div>
                      <div>
                        <h3 className="font-display text-base text-white font-light">{cert.topic}</h3>
                        <p className="font-sans text-xs text-cinema-muted mt-0.5">
                          {new Date(cert.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <p className="font-mono text-xs text-cinema-muted/80 mt-0.5">{cert.certificate_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleViewCert(cert.certificate_number)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans cinema-glass border border-white/10 text-cinema-muted hover:text-white hover:border-white/20 transition-all font-medium"
                        title="View Certificate"
                      >
                        <ExternalLink size={13} /> View
                      </button>
                      <button
                        onClick={() => handleDownloadCert(cert.certificate_number)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-cinema-glow/10 border border-cinema-glow/20 text-cinema-glow hover:bg-cinema-glow/20 hover:border-cinema-glow/40 transition-all font-medium"
                        title="Download Certificate"
                      >
                        <Download size={13} /> PDF
                      </button>
                    </div>
                  </div>
                  )
                })
              )}
            </div>
          )}

          {/* ── Bookmarks ── */}
          {activeTab === 'bookmarks' && (
            <div className="cinema-list">
              {(library?.bookmarks || []).length === 0 ? emptyState('No bookmarks yet.') : (
                (library?.bookmarks || []).map((bm) => (
                  <div key={bm.id} className="cinema-list-row justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-cinema-glow shrink-0"><Bookmark size={15} /></div>
                      <div>
                        <p className="font-sans text-sm text-white capitalize font-medium">{bm.item_type}</p>
                        <p className="font-mono text-xs text-cinema-muted">ID: {bm.item_id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeBookmark(bm.id)}
                      className="p-2 text-cinema-muted hover:text-red-400 transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Downloads ── */}
          {activeTab === 'downloads' && (
            <div className="cinema-list">
              {(library?.downloads || []).length === 0 ? emptyState('No downloads yet.') : (
                (library?.downloads || []).map((dl) => (
                  <div key={dl.id} className="cinema-list-row justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-cinema-glow shrink-0"><FileText size={16} /></div>
                      <div className="min-w-0">
                        <p className="font-sans text-sm text-white truncate font-medium">{dl.resource_title}</p>
                        <p className="font-sans text-xs text-cinema-muted uppercase tracking-wide mt-0.5">{dl.resource_type}</p>
                      </div>
                    </div>
                    <a
                      href={dl.file_url}
                      target="_blank" rel="noreferrer"
                      onClick={() => trackDownload(dl.resource_id)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-sans bg-cinema-glow/10 text-cinema-glow border border-cinema-glow/25 hover:bg-cinema-glow/20 transition-all shrink-0 font-medium"
                    >
                      <Download size={13} /> Download
                    </a>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Leaderboard ── */}
          {activeTab === 'leaderboard' && (
            <div className="cinema-list">
              {leaderboard.length === 0 ? emptyState('No leaderboard data yet.') : (
                leaderboard.map((entry, i) => {
                  const isTop3 = i < 3
                  const isYou = entry.email === user?.email
                  const rankCls = i === 0
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : i === 1
                    ? 'bg-white/10 text-white/70 border border-white/15'
                    : i === 2
                    ? 'bg-amber-700/20 text-amber-400 border border-amber-700/30'
                    : 'bg-white/5 text-cinema-muted border border-white/8'
                  return (
                    <div
                      key={i}
                      className={`cinema-list-row ${isYou ? 'border-l-2 border-l-cinema-glow/40' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-7 h-7 rounded-sm flex items-center justify-center font-mono text-xs ${rankCls} shrink-0`}>
                          {i === 0 ? <Crown size={12} /> : i + 1}
                        </div>
                        <div>
                          <p className={`font-sans text-sm sm:text-base ${isYou ? 'text-cinema-glow font-medium' : 'text-white font-medium'}`}>
                            {entry.first_name} {entry.last_name}
                            {isYou && <span className="ml-2 text-xs text-cinema-muted font-normal">(You)</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 font-sans text-sm text-cinema-glow font-semibold shrink-0">
                        <Trophy size={14} /> {entry.attended}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

